import { makeMnErrorView } from "common/components/error";
import { FetchErrorRetry } from "common/helpers/errors";
import { _ } from "extensions/underscore";

import strings from "common/strings";

import { Qualifiers } from "common/components/queryBuilder/qualifiers";

import { DefaultView } from "../defaultView";
import template from "./quickView.jst";

import { QuickAreaChart } from "./quickAreaChart";
import { QuickSummaryWithChange } from "./quickSummaryWithChange";
import { fetchChartData, basicDrillThrough, getDateRangesFromQueryParams } from "../widgetHelper";

export const QuickView = DefaultView.extend({
    type: "quick",
    updateNote: false,
    template,
    regions: {
        chart: ".chart",
        summary: ".widgetValue"
    },
    
    ui: {
        widgetValue: ".widgetValue",
        widgetNote: ".widgetNote",
        ...DefaultView.prototype.ui
    },
    
    events: {
        "click .grid-stack-item-content": "drillDownCompare",
        ...DefaultView.prototype.events
    },
    
    drillDownCompare() {
        // Don't drilldown if widget is moving, resizeing, or event is triggered when selecting text
        if (this.isClicking === false || this.isResizing || this.isDragging) {
            return;
        }
        
        const props = {
            urlRoot: this.model.subtypeModel.get("urlRoot"),
            dateRanges: getDateRangesFromQueryParams(this.collection.queryParams),
            widgetQualifiers: new Qualifiers({ value: this.filterInfo.mergedString })
        };
        basicDrillThrough(props);
    },
    
    templateContext() {
        const filterNotification = this.getIndicatorInfo();
        return {
            editable: "edit",
            indicator: filterNotification.cname,
            tt: filterNotification.tooltip
        };
    },
    
    setDataCollection(...args) {
        const widgetCollectionParams = this.getOption("parent").getViewCollection({
            url: _.result(this, "getAPIEndpoint"),
            params: this.getRequestParams(),
            mode: this.getCollectionSortMode()
        });
    
        this.collection = widgetCollectionParams.collection;
        this.collection.queryParams.q = this.mergeFilters();
        this.skipReactOnChanges = !widgetCollectionParams.isNew;

        this.listenTo(this.collection, "before:fetched", () => {
            this.addSpinners();
        });

        this.listenTo(this.collection, "after:fetched", () => {
            this.updateValues();
            this.updateWidgetTitle();
        });
    
        this.listenTo(this.collection, "fetched", this.makeContent);

        if (this.skipReactOnChanges) {
            this.addSpinners();
            this.updateValues();
            this.updateWidgetTitle();
        }
    },

    makeContent() {
        const widgetQualifiers = new Qualifiers({ value: this.filterInfo.mergedString });

        const options = {
            dateRanges: getDateRangesFromQueryParams(this.collection.queryParams),
            widgetContainer: this.el,
            widgetType: this.model.get("widgetType"),
            widgetSubType: this.model.get("widgetSubtype"),
            widgetQualifiers,
            subModel: this.model.subtypeModel,
            chartHeight: this.$el.height(),
            by: this.model.get("options").by,
            skipPivots: true
        };

        this.showChildView("summary", <QuickSummaryWithChange {...options}/>, {
            waitForAttach: true,
            dumb: true
        });

        if (this.existingPromiseRequest) {
            this.existingPromiseRequest.cancel();
        }

        this.existingPromiseRequest = fetchChartData(options.dateRanges[0], options).then((data) => {
            this.removeSpinners();
            options.data = data;
            this.showChildView("chart", <QuickAreaChart {...options}/>, {
                waitForAttach: true,
                dumb: true
            });
            this.collection.trigger("react_fetched", this);
        }, (error) => {
            if (!this.isDestroyed()) {
                this.removeSpinners();
                this.showChildView("chart", makeMnErrorView(new FetchErrorRetry({
                    message: "Please refresh widget as data could not load",
                    customActions: [{
                        className: "retry-widget",
                        label: "Refresh widget"
                    }],
                    hideDetails: true,
                    custom: true,
                    originalError: error
                }), true, false));
            }
        });
    },
    
    refreshWidgetOnEdit(force) {
        this.updateWidgetTitle(true);
        this.mergeFilters();
        this.collection.trigger("react_fetched", this);
    },
    
    updateWidgetTitle(forceUpdate) {
        const byOption = _.propValue(this.model.subtypeModel.getClone("quick"), "data.widgetNote");
        // Needed to make it backwards compatible..once user edits the title no longer add the byOption
        if ((!this.titleUpdated && this.model.get("w") > 2 && byOption) || forceUpdate) {
            const title = this.model.get("userEditedTitle") ? this.model.get("title") : `${this.model.get("title")} ${byOption}`;
            this.$(".title").html(title);
            this.titleUpdated = true;
        }
    },
    
    updateScale() {
        this.$(".widgetValue").css("transform", `scale(${Math.max(DefaultView.getScaleCoefficient(), 1)})`);
    },
    
    // Called from parent gridstack:init
    onGridstackInit() {
        this.updateScale();
        this.loadData();
    },
    
    onDestroy(...args) {
        if (this.existingPromiseRequest) {
            this.existingPromiseRequest.cancel();
        }
    },
    
    showErrorMessage() {
        this.updateWidgetTitle();
        
        if (this.getChildView("chart")) {
            this.getChildView("chart").hideLoader();
        }
        this.ui.widgetValue.html(`<div class="error-message">${strings.dashboardWidgetError}</div>`);
    }
});
